import { Plugins } from '@capacitor/core';
import '@/webPlugins/AnalyticsPlugin';

const { AnalyticsPlugin } = Plugins;

// Import statement for this file:
// import analytics from '@/utils/Analytics';

const analytics = {
  /*
   * BASE FUNCTIONS
   * Call Analytics Plugin
   */

  setAnalyticsEnabled(enabled) {
    try {
      AnalyticsPlugin.setAnalyticsCollectionEnabled(enabled);
    } catch (error) {
      console.error('AnalyticsPlugin error', error);
    }
  },

  setUserId(uid = '') {
    try {
      AnalyticsPlugin.setUserId({ userId: uid });
    } catch (error) {
      console.error('AnalyticsPlugin error', error);
    }
  },

  setUserProperty(name, value) {
    // console.log('setUserProperty', name, value);
    try {
      AnalyticsPlugin.setUserProperty({ name, value: `${value}` }); // value should be string
    } catch (error) {
      console.error('AnalyticsPlugin error', error);
    }
  },

  setCurrentScreen(name, param = '', category, isPremiumContent = false) {
    if (!name) return;
    const screenClass = name;
    let screenName = param ? `${name} ${param}` : name;
    if (name === 'Card' || name === 'AudioCard') {
      const categoryLetter = category.charAt(0).toUpperCase();
      screenName = `${name} ${categoryLetter} ${param}`;
    }
    if (isPremiumContent) screenName += ' +';

    try {
      AnalyticsPlugin.setCurrentScreen({ screenName, screenClass });
    } catch (error) {
      console.error('AnalyticsPlugin error', error);
    }
  },

  logEvent(eventName, eventParams = null) {
    try {
      // AnalyticsPlugin.logEvent({ eventName, eventParams: JSON.stringify(eventParams) });
      AnalyticsPlugin.logEvent({ eventName, eventParams });
    } catch (error) {
      console.error('AnalyticsPlugin error', error);
    }
  },

  // Default event for one-off events that just need to log event name, no params
  // Includes 'new_member_welcome_done'
  simpleEvent(eventName) {
    this.logEvent(eventName, { value: 1 });
  },

  /*
   * USER EVENTS
   */

  userSignedIn(uid, isNewUser, signInMethod) {
    const eventName = isNewUser ? 'sign_up' : 'login';
    const eventParams = {
      event_label: signInMethod,
      value: 1,
    };
    this.setUserId(uid);
    this.logEvent(eventName, eventParams);
  },

  userUpgradedAnonymous(signInMethod) {
    const eventName = 'upgrade_anonymous';
    const eventParams = {
      event_label: signInMethod,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  clickedUpgradeCTA(ctaLocation) {
    const eventName = 'upgrade_anon_cta_click';
    const eventParams = {
      description: ctaLocation,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  userSignedOut() {
    this.logEvent('logout', { value: 0 });
  },

  userDeletedAccount() {
    this.logEvent('delete_account', { value: 0 });
  },

  /*
   * EXCEPTIONS (non-fatal errors)
   */

  // Log errors that affect user experience
  exception(description) {
    const eventName = 'exception';
    const eventParams = {
      description,
      fatal: false,
      value: 0,
    };
    this.logEvent(eventName, eventParams);
  },

  /*
   * SUBSCRIPTION EVENTS
   */

  setSubscriberProperty(memberLevel) {
    this.setUserProperty('memberLevel', memberLevel);
  },

  paywallView({ feature, price, currency }) {
    const eventName = 'paywall_view';
    const eventParams = {
      description: feature,
      value: 1,
      price, // ex: '$1.99 / $4.99'
      currency,
    };
    this.logEvent(eventName, eventParams);
  },

  checkoutStart({ productId, price, currency }) {
    const eventName = 'checkout_start';
    const eventParams = {
      product_id: productId,
      value: 1,
      price,
      currency,
    };
    this.logEvent(eventName, eventParams);
  },

  checkoutCancel({ productId, price, currency }) {
    const eventName = 'checkout_cancel';
    const eventParams = {
      product_id: productId,
      value: 1,
      price,
      currency,
    };
    this.logEvent(eventName, eventParams);
  },

  checkoutError({ error, productId, price, currency }) {
    const eventName = 'checkout_error';
    const eventParams = {
      description: error,
      product_id: productId,
      value: 1,
      price,
      currency,
    };
    this.logEvent(eventName, eventParams);
  },

  restorePurchaseClick() {
    const eventName = 'restore_purchase_click';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  addNameToVisionaries() {
    const eventName = 'add_name_visionaries';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  /*
   * CONTENT EVENTS
   */

  dailyCardClick(timeOfDay) {
    const eventName = 'daily_card_click';
    const eventParams = {
      event_label: timeOfDay,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  // Track when a user an item from a list like category favorites or recent Stacks
  // Not currently tracking Daily Cards or clicks on Cards from a Stack
  contentListClick(setName) {
    if (!setName) return;
    const eventName = 'content_list_click';
    const eventParams = {
      event_label: setName,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  saveFavorite(type = 'card') {
    const eventName = `favorite_save_${type}`;
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  removeFavorite(type = 'card') {
    const eventName = `favorite_remove_${type}`;
    const eventParams = {
      value: 0,
    };
    this.logEvent(eventName, eventParams);
  },

  randomCardClick() {
    const eventName = 'random_card_click';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  // Actions:
  // stack_create_original, stack_create_adapted,
  // stack_add_card, stack_edit, stack_delete
  stackAction(action) {
    const eventName = `stack_${action}`;
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  /*
   * AUDIO
   */

  trackPlayed() {
    const eventName = 'audio_track_played';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  autoplayChanged(isAutoplayOn) {
    const eventName = `autoplay_${isAutoplayOn ? 'on' : 'off'}`;
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  sleepTimerSet(sleepNum) {
    const eventName = `sleep_timer_${sleepNum > 0 ? 'on' : 'off'}`;
    const eventParams = {
      description: sleepNum,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  increaseSleepTimer(sleepNum) {
    const eventName = 'sleep_timer_increase';
    const eventParams = {
      description: sleepNum,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  customizeSleepOption(changedOption) {
    const eventName = 'sleep_timer_customize';
    const eventParams = {
      description: JSON.stringify(changedOption),
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  /*
   * GET STARTED EVENTS
   */
  // Actions: 'slide', 'cta_click'
  authIntroAction(action, slideNum) {
    const eventName = `auth_intro_${action}`;
    const eventParams = {
      eventLabel: slideNum,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  // Actions: 'slide_1', 'done', 'reopen'
  welcomeAction(action) {
    const eventName = `welcome_${action}`;
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  /*
   * EXTERNAL LINKS
   */

  socialClick(channel) {
    const eventName = `social_click_${channel}`;
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  // Share content from app
  share(contentType = 'app', id = 'app') {
    const eventName = 'share';
    const eventParams = {
      content_type: contentType,
      item_id: id,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  shareViewed(contentType, id) {
    const eventName = 'share_viewed';
    const eventParams = {
      content_type: contentType,
      item_id: id,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  /**
   * MARKETING
   */

  scrolledToSection(name) {
    const eventName = `scrolled_to_${name}`;
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  appStoreClick(platform, location) {
    const eventName = `app_store_click_${platform}`;
    const eventParams = {
      description: location,
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  marketingTopBarCTAClick() {
    const eventName = 'marketing_topbar_cta_click';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  marketingEmailSignUp() {
    const eventName = 'marketing_email_sign_up';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },

  marketingEmailConfirmed() {
    const eventName = 'marketing_email_confirmed';
    const eventParams = {
      value: 1,
    };
    this.logEvent(eventName, eventParams);
  },
};

export default analytics;
